import { useQuery } from "@tanstack/react-query";
import BigNumber from "bignumber.js";

import { useGetAccountInfo } from "./sdkDappHooks";
import { MultiversxService } from "../services/mvx.service";
import type { TokenType } from "../types/token.types";
import { A1X_TOKEN } from "../config/sharedConfig";

export const useGetAccountTokens = () => {
  const { address } = useGetAccountInfo();

  return useQuery({
    queryKey: ["account-tokens", address],
    queryFn: async () => {
      const tokens = await MultiversxService.getTokens(address);

      return tokens.sort((a: TokenType, b: TokenType) => {
        const amountA = BigNumber(a.balance ?? 0)
          .dividedBy(10 ** a.decimals)
          .times(a.price ?? 0);
        const amountB = BigNumber(b.balance ?? 0)
          .dividedBy(10 ** b.decimals)
          .times(b.price ?? 0);
        return amountB.minus(amountA).toNumber();
      });
    },
    enabled: !!address,
  });
};

// Hook specifically for A1X token balance
export const useGetA1XBalance = () => {
  const { address } = useGetAccountInfo();

  return useQuery({
    queryKey: ["a1x-balance", address],
    queryFn: async () => {
      const tokens = await MultiversxService.getTokens(address);

      // Find A1X token
      const a1xToken = tokens.find(
        (token) =>
          token.identifier === A1X_TOKEN ||
          token.ticker === "A1X" ||
          token.name?.toLowerCase().includes("a1x")
      );

      if (!a1xToken) {
        return {
          balance: "0",
          formattedBalance: "0",
          token: null,
        };
      }

      const formattedBalance = BigNumber(a1xToken.balance ?? 0)
        .dividedBy(10 ** a1xToken.decimals)
        .toFixed(2);

      return {
        balance: a1xToken.balance ?? "0",
        formattedBalance,
        token: a1xToken,
      };
    },
    enabled: !!address,
  });
};
